"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useAuth } from "@/providers/auth-provider";
import { discussionsApi } from "@/lib/api/services/discussions.api";
import { eventsApi } from "@/lib/api/services/events.api";
import { learningApi } from "@/lib/api/services/learning.api";
import { activityApi } from "@/lib/api/services/activity.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/avatar";
import { SkeletonList } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import {
  MessageSquare,
  Calendar,
  BookOpen,
  TrendingUp,
  ArrowRight,
  Clock,
  Activity,
  Sparkles,
  Bell,
} from "lucide-react";

const MAX_WELCOME_LENGTH = 30;

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function getDayMessage(): string {
  const d = new Date().getDay();
  if (d === 0) return "Enjoy your Sunday";
  if (d === 6) return "Happy Saturday";
  if (d === 5) return "Almost the weekend";
  return "Your daily snapshot";
}

interface Discussion {
  id: string;
  title: string;
  replyCount: number;
  createdAt: string;
}

interface Event {
  id: string;
  title: string;
  date: string;
  location: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  duration: string;
}

function StatCard({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  href: string;
}) {
  return (
    <Link href={href}>
      <Card hover className="group">
        <CardContent className="flex items-center gap-4 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/10 text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-lg font-semibold tracking-tight text-foreground tabular-nums">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim().slice(0, MAX_WELCOME_LENGTH)
    : "there";
  const userInitials = user
    ? `${(user.firstName ?? "")[0] ?? ""}${(user.lastName ?? "")[0] ?? ""}`
    : "?";

  const {
    data: discussionsData,
    isLoading: discussionsLoading,
  } = useQuery({
    queryKey: ["home", "discussions"],
    queryFn: async () => {
      const res = await discussionsApi.list({ page: 1, perPage: 5 });
      return (res as unknown as { data: Discussion[] }).data ?? [];
    },
  });

  const {
    data: eventsData,
    isLoading: eventsLoading,
  } = useQuery({
    queryKey: ["home", "events"],
    queryFn: async () => {
      const res = await eventsApi.list({ page: 1, perPage: 5, status: "upcoming" });
      return (res as unknown as { data: Event[] }).data ?? [];
    },
  });

  const {
    data: coursesData,
    isLoading: coursesLoading,
  } = useQuery({
    queryKey: ["home", "courses"],
    queryFn: async () => {
      const res = await learningApi.listCourses({ page: 1, perPage: 5 });
      return (res as unknown as { data: Course[] }).data ?? [];
    },
  });

  const {
    data: activityData,
    isLoading: activityLoading,
  } = useQuery({
    queryKey: ["home", "activity"],
    queryFn: async () => {
      const res = await activityApi.listMine({ page: 1, perPage: 5 });
      const items = (res as { data: Array<Record<string, unknown>> }).data ?? [];
      return items.map((item) => ({
        id: String(item.id ?? ""),
        type: (item.action as string) || "notification",
        title: (item.title as string) || (item.action as string) || "",
        description: (item.description as string) || (item.entityType as string) || "",
        timestamp: formatRelativeTime((item.createdAt as string) || ""),
      }));
    },
  });

  const discussions = discussionsData ?? [];
  const events = eventsData ?? [];
  const courses = coursesData ?? [];
  const activities = activityData ?? [];

  const mostDiscussed: Discussion | null =
    discussions.length > 0
      ? discussions.reduce((prev, curr) =>
          (prev.replyCount ?? 0) > (curr.replyCount ?? 0) ? prev : curr,
        )
      : null;

  const isLoading = discussionsLoading || eventsLoading || coursesLoading || activityLoading;

  return (
    <div className="space-y-10 animate-fade-in">
      {/* Welcome */}
      <section className="space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow flex items-center gap-2 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Dashboard
            </div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground">
              {getGreeting()}, {displayName}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{getDayMessage()}</p>
          </div>
          <Avatar
            src={user?.avatarUrl}
            alt={displayName}
            fallback={userInitials}
          />
        </div>
      </section>

      {/* Stats */}
      {!isLoading && (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={MessageSquare} label="Discussions" value={discussions.length} href="/discussions" />
          <StatCard icon={Calendar} label="Upcoming Events" value={events.length} href="/events" />
          <StatCard icon={BookOpen} label="Active Courses" value={courses.length} href="/learning" />
          <StatCard icon={Activity} label="Recent Activity" value={activities.length} href="/feed" />
        </section>
      )}

      {/* Loading skeleton for stats */}
      {isLoading && (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="h-10 w-10 rounded-lg bg-muted/10 animate-pulse" />
                <div className="space-y-2">
                  <div className="h-5 w-12 bg-muted/10 rounded animate-pulse" />
                  <div className="h-3 w-16 bg-muted/10 rounded animate-pulse" />
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {/* Main Content */}
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Activity Feed */}
        <div className="lg:col-span-2 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-foreground">Recent Activity</h2>
            <Button variant="ghost" size="sm" asChild>
              <Link href="/feed">
                View all <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-4">
                  <SkeletonList items={5} />
                </div>
              ) : activities.length === 0 ? (
                <div className="py-12">
                  <EmptyState
                    title="No recent activity"
                    description="Your activity feed will appear here as you interact with the platform."
                    icon={<Bell className="h-8 w-8 text-muted-foreground/40" />}
                  />
                </div>
              ) : (
                <div className="divide-y divide-border/60">
                  {activities.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-start gap-3 p-4 transition-colors hover:bg-muted/10"
                    >
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
                        <Sparkles className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{a.title}</p>
                        <p className="truncate text-xs text-muted-foreground mt-0.5">{a.description}</p>
                        <p className="mt-1 text-xs text-muted-foreground/60">{a.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Widgets */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-semibold text-foreground">Upcoming Events</h2>
              <Button variant="ghost" size="xs" asChild>
                <Link href="/events">View all</Link>
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-3"><SkeletonList items={3} /></div>
                ) : events.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No upcoming events
                  </div>
                ) : (
                  <div className="divide-y divide-border/60">
                    {events.slice(0, 3).map((e) => (
                      <Link key={e.id} href={`/events/${e.id}`} className="block p-3 transition-colors hover:bg-muted/10">
                        <p className="text-sm font-medium text-foreground hover:text-primary transition-colors">{e.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{e.date} &middot; {e.location}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Recent Discussions */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-semibold text-foreground">Discussions</h2>
              <Button variant="ghost" size="xs" asChild>
                <Link href="/discussions">View all</Link>
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-3"><SkeletonList items={3} /></div>
                ) : discussions.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No discussions yet
                  </div>
                ) : (
                  <div className="divide-y divide-border/60">
                    {discussions.slice(0, 3).map((d) => (
                      <Link key={d.id} href={`/discussions/${d.id}`} className="block p-3 transition-colors hover:bg-muted/10">
                        <p className="text-sm font-medium text-foreground hover:text-primary transition-colors">{d.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{d.replyCount} replies</p>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Featured Courses */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-semibold text-foreground">Courses</h2>
              <Button variant="ghost" size="xs" asChild>
                <Link href="/learning">View all</Link>
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-3"><SkeletonList items={3} /></div>
                ) : courses.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No courses available
                  </div>
                ) : (
                  <div className="divide-y divide-border/60">
                    {courses.slice(0, 3).map((c) => (
                      <Link key={c.id} href={`/learning/courses/${c.id}`} className="block p-3 transition-colors hover:bg-muted/10">
                        <p className="text-sm font-medium text-foreground hover:text-primary transition-colors">{c.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{c.duration}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        </div>
      </div>

      {/* Trending CTA */}
      {mostDiscussed && (
        <Card variant="highlight" className="group transition-all hover:bg-primary/[0.04]">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <Badge variant="premium" size="sm" className="mb-1.5">Trending</Badge>
              <p className="text-sm font-medium text-foreground line-clamp-1">{mostDiscussed.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{mostDiscussed.replyCount} replies</p>
            </div>
            <Button size="sm" variant="premium" asChild>
              <Link href="/discussions">
                Join <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
